/**
 * @fileOverview The Erklärung's camera: where it looks, how close, and how it gets from one view to
 * the next. Pure math — no Vue, no DOM. See docs/view-help.md.
 *
 * The frame is rendered at a fixed size and one transform on the layer holding it both fits it to the
 * stage and zooms onto a region: `translate(tx, ty) scale(s)`, from the top-left corner. So a camera
 * is just those three numbers, and a point in the frame lands on the stage at `p * s + t`.
 */

import type { Rect } from "@/services/viewHelp/target"

/**
 * The frame's own viewport, in CSS pixels. 1280 is above `NAV_OVERLAY_BREAKPOINT` (1264) on purpose:
 * the app inside therefore always renders a permanent navigation drawer, whatever the reader's window
 * is doing.
 */
export const FRAME_WIDTH = 1280
export const FRAME_HEIGHT = 820

/**
 * How close the camera goes: until the region fills about this share of the stage, never past
 * `ZOOM_MAX` — close enough that a tab reads as the subject, not so close that it loses the page
 * around it — and never below 1, which is the whole page.
 */
export const ZOOM_FILL = 0.75
export const ZOOM_MAX = 2.1

export interface Camera {
  /** Scale from frame pixels to stage pixels — the fit to the stage times the zoom. */
  s: number
  tx: number
  ty: number
}

const clamp = (min: number, value: number, max: number) => Math.min(Math.max(value, min), max)

/** A stage length as CSS, rounded to a tenth of a pixel so a style changes only when the picture does. */
export const px = (value: number) => `${Math.round(value * 10) / 10}px`

/** How far the camera zooms onto a region. 1 for no region — the whole page. */
export function zoomFor(rect: Rect | null, cap: number = ZOOM_MAX): number {
  if (!rect || rect.width <= 0 || rect.height <= 0) return 1
  const fill = Math.min((FRAME_WIDTH * ZOOM_FILL) / rect.width, (FRAME_HEIGHT * ZOOM_FILL) / rect.height)
  return clamp(1, fill, Math.min(ZOOM_MAX, cap))
}

/**
 * The camera that shows a region: zoomed by `zoomFor`, its centre in the middle of the stage — moved
 * only as far as keeps the frame's edges out of view, so a tab near the top is shown against the top
 * edge rather than centred over nothing.
 */
export function cameraFor(rect: Rect | null, fit: number, cap?: number): Camera {
  const s = fit * zoomFor(rect, cap)
  if (!rect || rect.width <= 0 || rect.height <= 0) return { s, tx: 0, ty: 0 }

  const width = FRAME_WIDTH * fit
  const height = FRAME_HEIGHT * fit
  const cx = rect.left + rect.width / 2
  const cy = rect.top + rect.height / 2
  return {
    s,
    tx: clamp(width - FRAME_WIDTH * s, width / 2 - cx * s, 0),
    ty: clamp(height - FRAME_HEIGHT * s, height / 2 - cy * s, 0),
  }
}

/** The frame point in the middle of the stage — where the camera is looking. */
function lookAt(camera: Camera, fit: number) {
  return {
    x: ((FRAME_WIDTH * fit) / 2 - camera.tx) / camera.s,
    y: ((FRAME_HEIGHT * fit) / 2 - camera.ty) / camera.s,
  }
}

/**
 * The camera part of the way, `e`, from `from` to `to`.
 *
 * Not the CSS way. A transition on `translate() scale()` interpolates the offset and the scale each on
 * their own, which zooms about whatever point happens to stay fixed — and with the offsets pinned to
 * the frame's edges, that point sat on the top edge vertically and in the middle horizontally: the
 * picture grew one way across and another way down, which is exactly what looked wrong.
 *
 * Instead, what the camera *looks at* moves in a straight line, both axes together, and the zoom
 * changes geometrically — by the same ratio every frame, which is what reads as an even speed; a
 * linear scale rushes at the zoomed-out end and crawls at the other. It is the way map and presentation
 * software moves a camera.
 *
 * It also needs no clamping on the way. The view is inside the frame when its centre, give or take
 * half its width, is — a condition linear in the centre and the width. The centre moves linearly and
 * a geometric width is never wider than the linear one between the same two ends, so if both ends
 * show only the frame, so does every frame in between.
 */
export function interpolateCamera(from: Camera, to: Camera, e: number, fit: number): Camera {
  if (e <= 0) return from
  if (e >= 1) return to

  const a = lookAt(from, fit)
  const b = lookAt(to, fit)
  const s = from.s * Math.pow(to.s / from.s, e)
  const x = a.x + (b.x - a.x) * e
  const y = a.y + (b.y - a.y) * e

  return { s, tx: (FRAME_WIDTH * fit) / 2 - x * s, ty: (FRAME_HEIGHT * fit) / 2 - y * s }
}

/** The camera's easing: slow out, slow in. Matches the curve the CSS transition used. */
export const easeCamera = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2)
